<template>
  <PopoverOptions :hide-delete="hideDelete" @add="handleAdd" @delete="handleDelete">
    <div class="card">
      <a-form ref="formRef" layout="vertical" :model="formState">
        <a-form-item class="input" name="input_name" required>
          <template #label>
            Campo
            <a-tooltip title="Los campos deshabilitados no pueden ser usados para condicionales.">
              <InfoCircleOutlined style="margin-left: 5px" />
            </a-tooltip>
          </template>

          <a-select
            :getPopupContainer="(triggerNode) => triggerNode.parentNode"
            v-model:value="formState.input_name"
            placeholder="Seleccionar un campo"
          >
            <a-select-option
              v-for="field in fieldOptions"
              :key="field.name"
              :disabled="field.disabled"
            >
              <a-tooltip :title="getTooltipField(field)" placement="right">
                <div style="width: 100%">
                  {{ field.label }}
                  <span class="type-name">
                    ({{ field.is_range ? "Rango de " : "" }}{{ getNameByType(field.type) }})
                  </span>
                </div>
              </a-tooltip>
            </a-select-option>
          </a-select>

          <span class="description" v-if="formState.input_validation === ValidationType.Email">
            Este elemento solo permite respuestas de tipo correo electrónico
          </span>
        </a-form-item>

        <div class="row">
          <a-form-item label="Condición" name="condition_type" required>
            <a-select
              :getPopupContainer="(triggerNode) => triggerNode.parentNode"
              v-model:value="formState.condition_type"
              placeholder="Seleccionar una condición"
              :disabled="!formState.input_name"
              :options="conditionConfig?.options"
            />
          </a-form-item>

          <a-form-item
            label="Valor"
            name="comparison_value"
            :required="showDisabledComparisonValue ? false : conditionConfig?.comparisonValueType"
            :rules="comparisonValueRules"
          >
            <a-input v-if="showDisabledComparisonValue" placeholder="No Aplica" disabled />
            <a-select
              :getPopupContainer="(triggerNode) => triggerNode.parentNode"
              v-else-if="conditionConfig?.comparisonValueType === ComparisonValueType.Option"
              v-model:value="formState.comparison_value"
              placeholder="Seleccionar valor aquí..."
              :disabled="!formState.condition_type"
              :options="conditionConfig.valueOptions"
            />
            <a-date-picker
              v-else-if="conditionConfig?.comparisonValueType === ComparisonValueType.Date"
              v-model:value="formState.comparison_value"
              placeholder="--/--/----"
              format="DD/MM/YYYY"
              valueFormat="YYYY-MM-DD"
              :disabled="!formState.condition_type"
            />
            <a-time-picker
              v-else-if="conditionConfig?.comparisonValueType === ComparisonValueType.Time"
              v-model:value="formState.comparison_value"
              placeholder="--:--"
              format="HH:mm"
              valueFormat="HH:mm"
              :disabled="!formState.condition_type"
            />
            <a-input
              v-else
              v-model:value="formState.comparison_value"
              placeholder="Escribir valor aquí..."
              :disabled="!formState.condition_type"
            />
          </a-form-item>
        </div>
      </a-form>
    </div>
  </PopoverOptions>
</template>

<script setup lang="ts">
import { reactive, watch, computed, ref } from "vue";
import { InfoCircleOutlined } from "@ant-design/icons-vue";
import { FormInstance } from "ant-design-vue";

import {
  ComparisonValueType,
  ConditionType,
  ElementType,
  FormElement,
  FormIf,
  isText,
  ValidationType,
} from "@/@types/entities/forms";
import { formConditionsManager } from "@/app/entities/utils/forms/FormConditionsManager";
import { getNameByType } from "@/app/entities/utils/forms";
import { getConditionInputsByElement } from "@/app/entities/utils/forms/conditionsSelectOptions";
import { Option } from "@/@types/entities/forms/manager";

import PopoverOptions from "@/app/entities/components/card/conditions/PopoverOptions.vue";
import { ValidatorByType } from "@/app/entities/utils/forms/textValidators";

const props = defineProps<{
  formIf: FormIf;
  hideDelete: boolean;
  inputsList?: FormElement[];
}>();

const emit = defineEmits<{
  (e: "update:formIf", formIf: FormIf): void;
  (e: "add", id: string): void;
  (e: "delete", id: string): void;
}>();

const formState: FormIf = reactive({
  id: props.formIf.id,
  input_name: props.formIf.input_name,
  input_label: props.formIf.input_label,
  input_type: props.formIf.input_type,
  input_validation: props.formIf.input_validation,
  condition_type: props.formIf.condition_type,
  comparison_value: props.formIf.comparison_value,
});

watch(formState, () => emit("update:formIf", formState));
watch(
  () => formState.input_name,
  () => {
    formState.input_label = selectedInputElement.value?.label;
    formState.input_type = selectedInputElement.value?.type;
    formState.input_validation = isText(selectedInputElement.value)
      ? selectedInputElement.value?.validation
      : null;
    formState.condition_type = undefined;
    formState.comparison_value = undefined;
  }
);

const handleAdd = () => emit("add", props.formIf.id);
const handleDelete = () => emit("delete", props.formIf.id);

const fieldOptions = computed(() =>
  formConditionsManager.getInputOptionsByElement("if", props.inputsList)
);

const getTooltipField = (option: Option) => {
  if (!option.disabled) return false;

  if (option.type === ElementType.Header)
    return "Los elementos sin campos no pueden iniciar una condicional.";

  if ([ElementType.Reminder, ElementType.Pdp].includes(option.type))
    return "Los elementos que contienen varios campos no pueden iniciar una condicional.";

  return false;
};

const selectedInputElement = computed(() =>
  props.inputsList?.find((input) => input.name === formState.input_name)
);

const conditionConfig = computed(() => {
  return getConditionInputsByElement(selectedInputElement.value);
});

const showDisabledComparisonValue = computed(() => {
  const isFile = conditionConfig.value?.comparisonValueType === ComparisonValueType.File;
  const conditionNotHaveComparison = formState.condition_type
    ? [ConditionType.IsEmpty, ConditionType.NoEmpty].includes(formState.condition_type)
    : false;

  return isFile || conditionNotHaveComparison;
});

const comparisonValueRules = computed(() => [
  {
    required: showDisabledComparisonValue.value
      ? false
      : conditionConfig.value?.comparisonValueType,
    validator: formState.input_validation ? ValidatorByType[formState.input_validation] : false,
  },
]);

// Form validation
const formRef = ref<FormInstance | undefined>(undefined);

const validate = async () => {
  await formRef.value?.validateFields();
};

defineExpose({ validate });
</script>

<style scoped lang="sass">
.card
  width: 100%
  background-color: white
  border-radius: 4px
  padding: 20px

.ant-form-item
  margin: 0

.input
  margin-bottom: 20px

.row
  display: grid
  grid-template-columns: 1fr 1fr
  gap: 0 12px

.type-name
  color: $gray-6

.description
  color: $gray-7
</style>
